'use client';

import { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { css } from '@emotion/react';
import { Android, Apple } from '@mui/icons-material';
import {
  Typography,
  Stack,
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  Link,
  Divider,
  Chip,
} from '@mui/material';
import { AppBarLogo, Image, useItems } from '@scorenco/components';
import { BaseLayout, NextPageWithLayout } from '@scorenco/next';
import { format, isToday } from 'date-fns';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { GetTournamentsDocument } from './_internal/index.query.generated';

const Home: NextPageWithLayout = () => {
  const router = useRouter();
  const [limit, setLimit] = useState(5);

  const getTournamentsQuery = useQuery(GetTournamentsDocument, {
    variables: {
      limit,
    },
    context: {
      role: 'anonymous',
    },
  });

  const tournaments = useItems(
    getTournamentsQuery.data?.tournaments_tournament_index,
    5
  );

  const handleLoadMore = () => {
    setLimit(limit + 3);
  };

  return (
    <>
      <Head>
        <title key="title">Tournois - Score'n'co</title>
      </Head>

      <AppBar>
        <Toolbar>
          <Box
            component="button"
            type="button"
            onClick={() => router.push(`/`)}
            aria-label="Retour a l'accueil des tournois"
            sx={{
              border: 0,
              background: 'transparent',
              p: 0,
              cursor: 'pointer',
            }}
          >
            <AppBarLogo />
          </Box>

          <Box display={{ xs: 'none', md: 'block' }}>
            <Button
              component="a"
              href="https://scorenco.com"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="secondary"
            >
              Découvrir Score'n'co
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          paddingTop: '4rem',
          maxWidth: '100%',
        }}
      >
        <Container>
          <Stack spacing={4} sx={{ mt: 3, pb: 3, alignItems: 'center' }}>
            {!tournaments.length ? (
              <>
                <Typography
                  variant="body1"
                  sx={{ textAlign: 'center', maxWidth: '25rem' }}
                >
                  Il n'y a aucun tournoi en cours.
                  <br />
                  Reviens plus tard 😉
                </Typography>
                <Divider sx={{ width: '100%' }} />
              </>
            ) : (
              <Stack sx={{ width: '100%' }} spacing={2}>
                <Typography variant="h3">Tournoi(s) en cours</Typography>
                <Stack spacing={1}>
                  {tournaments?.[0]?.tournament &&
                    tournaments.map((tournamentIndex) => {
                      const tournament = tournamentIndex?.tournament;
                      const next_game_date =
                        typeof tournamentIndex?.next_game_date === 'string' ||
                        typeof tournamentIndex?.next_game_date === 'number'
                          ? tournamentIndex.next_game_date
                          : undefined;
                      const last_game_date =
                        typeof tournamentIndex?.last_game_date === 'string' ||
                        typeof tournamentIndex?.last_game_date === 'number'
                          ? tournamentIndex.last_game_date
                          : undefined;

                      if (!tournament) {
                        return null;
                      }

                      return (
                        <Button
                          key={
                            tournament.id ??
                            `${tournament.name}-${next_game_date}`
                          }
                          css={(theme) => css`
                            flex: 1;
                            width: 100%;
                            text-transform: none;
                            color: ${theme.vars.palette.primary.main};
                            background: white;
                            border-radius: 0.25rem;
                            border: 1px solid ${theme.vars.palette.divider};
                            position: relative;

                            &.mui-selected: {
                              border-color: ${theme.vars.palette.primary.main};
                            }

                            padding-left: 2rem;
                            padding-right: 2rem;
                            padding-top: 1rem;
                            padding-bottom: 1rem;

                            @keyframes pulse {
                              0% {
                                opacity: 0.4;
                              }
                              50% {
                                opacity: 1;
                              }
                              100% {
                                opacity: 0.4;
                              }
                            }

                            ${next_game_date &&
                            isToday(new Date(next_game_date)) &&
                            `
                              &::before {
                                content: '';
                                position: absolute;
                                top: 0.5rem;
                                left: 0.5rem;
                                width: 0.60rem;
                                height: 0.60rem;
                                background-color: #ff3b30;
                                animation: pulse 2s infinite ease-in-out;
                                border-radius: 50%;
                              }
                            `}
                          `}
                          onClick={() => {
                            router.push(`/${tournament.id}`);
                          }}
                        >
                          <Stack direction="row" gap={2} sx={{ width: '100%' }}>
                            {(tournament.logo || tournament.club?.logo) && (
                              <Image
                                src={tournament.logo || tournament.club?.logo}
                                css={css`
                                  width: 100%;
                                  max-width: 3rem;
                                  height: auto;
                                  object-fit: contain;
                                `}
                                width={100}
                                height={100}
                              />
                            )}
                            <Stack spacing={1} sx={{ width: '100%' }}>
                              <Typography
                                variant="h4"
                                component="h4"
                                sx={{ lineHeight: 1.2, fontSize: '1.1rem' }}
                              >
                                {tournament.name}
                              </Typography>
                              <Stack
                                spacing={4}
                                direction="row"
                                justifyContent="center"
                              >
                                <Typography
                                  variant="body2"
                                  component="p"
                                  sx={{ m: 0 }}
                                >
                                  {tournament.location}
                                </Typography>
                                <Chip
                                  component="p"
                                  css={css`
                                    ${next_game_date &&
                                    isToday(new Date(next_game_date)) &&
                                    `
                                    position: relative;
                                    padding-left: 1rem;
                                    &::before {
                                      content: '';
                                      position: absolute;
                                      top: 50%;
                                      left: 0.5rem;
                                      transform: translateY( -50%);
                                      width: 0.60rem;
                                      height: 0.60rem;
                                      background-color: #ff3b30;
                                      animation: pulse 2s infinite ease-in-out;
                                      border-radius: 50%;
                                    }
                                `}
                                  `}
                                  size="small"
                                  color="primary"
                                  label={
                                    next_game_date
                                      ? 'Prochain match : ' +
                                        (isToday(new Date(next_game_date))
                                          ? "Aujourd'hui"
                                          : format(
                                              new Date(next_game_date),
                                              'dd/MM/yyyy'
                                            ))
                                      : last_game_date &&
                                        'Dernier match : ' +
                                          (isToday(new Date(last_game_date))
                                            ? "Aujourd'hui"
                                            : format(
                                                new Date(last_game_date),
                                                'dd/MM/yyyy'
                                              ))
                                  }
                                />
                              </Stack>
                            </Stack>
                          </Stack>
                        </Button>
                      );
                    })}
                </Stack>
                {tournaments.length >= limit && (
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    style={{ margin: '0 auto', marginTop: '1rem' }}
                  >
                    Voir plus
                  </Button>
                )}
              </Stack>
            )}
            <Stack sx={{ width: '100%' }} spacing={2}>
              <Typography variant="h3">
                Télécharger l'application Score'n'co 📲
              </Typography>

              <Stack gap={1}>
                <a
                  href="https://apps.apple.com/fr/app/scorenco-sport-en-direct/id816395945"
                  target="_blank"
                  rel="noopener noreferrer"
                  css={(theme) => css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    width: 100%;
                    text-transform: none;
                    color: ${theme.vars.palette.primary.main};
                    background: white;
                    border-radius: 0.25rem;
                    border: 1px solid ${theme.vars.palette.divider};

                    &.mui-selected: {
                      border-color: ${theme.vars.palette.primary.main};
                    }

                    padding-left: 2rem;
                    padding-right: 2rem;
                    padding-top: 1rem;
                    padding-bottom: 1rem;
                    text-decoration: none;
                  `}
                >
                  <Apple sx={{ mr: 0.5 }} /> Application IOS
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.scorenco.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  css={(theme) => css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    width: 100%;
                    text-transform: none;
                    color: ${theme.vars.palette.primary.main};
                    background: white;
                    border-radius: 0.25rem;
                    border: 1px solid ${theme.vars.palette.divider};

                    &.mui-selected: {
                      border-color: ${theme.vars.palette.primary.main};
                    }

                    padding-left: 2rem;
                    padding-right: 2rem;
                    padding-top: 1rem;
                    padding-bottom: 1rem;
                    text-decoration: none;
                  `}
                >
                  <Android sx={{ mr: 0.5 }} /> Application Android
                </a>
              </Stack>
            </Stack>

            <Stack sx={{ width: '100%' }} spacing={1} alignItems="center">
              <Typography
                variant="h6"
                sx={{ textAlign: 'center', maxWidth: '25rem' }}
              >
                Votre club est organisateur de tournois ?<br />
                Contactez-nous pour tester notre solution automatisée de
                communication & gestion de tournois 🏆
              </Typography>
              <Typography
                variant="h5"
                sx={{ textAlign: 'center', maxWidth: '25rem' }}
              >
                <Link
                  href="mailto:contact@scorenco.com"
                  color="inherit"
                  underline="hover"
                  target="_blank"
                  rel="noopener"
                >
                  contact@scorenco.com
                </Link>
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </>
  );
};

export const getStaticProps = BaseLayout.buildGetStaticProps();

Home.getLayout = BaseLayout.buildGetLayout();

export default Home;
